import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/dramas";

export default function HeroCarousel({ items }: { items: Drama[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const current = items[index];

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden bg-primary-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.banner}
            alt={`${item.title} 焦点图`}
            title={`${item.title} 剧情介绍`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-xl animate-fade-up" key={current.id}>
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 text-xs rounded-md bg-accent-500 text-primary-950 font-medium">
              焦点推荐
            </span>
            <span className="px-2.5 py-1 text-xs rounded-md bg-background-50/15 text-background-50 backdrop-blur">
              {current.type}
            </span>
            <span className="text-sm text-accent-300 font-medium flex items-center gap-1">
              <i className="ri-star-fill" aria-hidden="true"></i>
              {current.rating.toFixed(1)}
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
            {current.title}
          </h2>
          <p className="text-sm md:text-base text-background-100/90 leading-relaxed line-clamp-3 mb-6">
            {current.synopsis}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="px-6 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看详情
            </Link>
            <Link
              to={`/category/${current.category}`}
              className="px-6 py-2.5 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              同类推荐
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary-950/40 text-background-50 backdrop-blur flex items-center justify-center hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary-950/40 text-background-50 backdrop-blur flex items-center justify-center hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-accent-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}